import { MODERATION_RULES, type AdminGenerationRow, type AdminUserCard } from '@trendator/shared';
import { useState } from 'react';
import { Link } from '../components/Layout';
import { Button, Card, Chip, Empty, ErrorBox, Field, Input, Label, Loading, Modal, Status, Table, Textarea, useToast } from '../components/ui';
import { cn } from '../lib/cn';
import { api, errorText } from '../lib/api';
import { date, dateTime, num, rub, shortId, signedRub } from '../lib/format';
import { useLoad } from '../lib/hooks';
import { GEN_STATUS, genSum } from './Generations';
import { METHOD, PAYOUT_STATUS } from './Partners';

const LEDGER: Record<string, { label: string; chip: string; amt: string }> = {
  hold: { label: 'списание', chip: 'bg-chip text-text2', amt: 'text-white' },
  release: { label: 'возврат', chip: 'bg-warn/14 text-[#FFCF85]', amt: 'text-[#FFCF85]' },
  topup: { label: 'пополнение', chip: 'bg-ok/12 text-[#8FE8BF]', amt: 'text-ok' },
  bonus: { label: 'бонус', chip: 'bg-ok/12 text-[#8FE8BF]', amt: 'text-ok' },
  referral: { label: 'партнёрка', chip: 'bg-ok/12 text-[#8FE8BF]', amt: 'text-ok' },
  withdrawal: { label: 'вывод', chip: 'bg-chip text-text2', amt: 'text-white' },
  adjustment: { label: 'корректировка', chip: 'bg-accent/14 text-[#A9D9FF]', amt: 'text-accent' },
};

export function UserCard({ id }: { id: string }) {
  const toast = useToast();
  const u = useLoad(() => api.get<AdminUserCard>(`/users/${id}`), [id]);
  const gens = useLoad(() => api.get<{ items: AdminGenerationRow[] }>(`/generations?userId=${id}`), [id]);
  const [tab, setTab] = useState<'ledger' | 'gens' | 'refs'>('ledger');
  const [dialog, setDialog] = useState<'ban' | 'message' | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (u.error && !u.data) return <ErrorBox text={u.error} onRetry={() => void u.reload()} />;
  if (!u.data) return <Loading />;
  const c = u.data;

  const run = async (name: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(name);
    try {
      const r = await fn();
      if (r && typeof r === 'object' && 'ledger' in r) u.setData(r as AdminUserCard);
      toast(ok);
      setDialog(null);
      return true;
    } catch (err) {
      toast(errorText(err), 'bad');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const tabs = [
    { id: 'ledger', label: 'Журнал баланса' },
    { id: 'gens', label: `Генерации ${c.generations}` },
    { id: 'refs', label: `Рефералы ${c.referrals.length}` },
  ] as const;

  return (
    <>
      <Label>
        <Link to="/users" className="text-muted">
          Юзеры
        </Link>{' '}
        / {c.username ? `@${c.username}` : c.firstName}
      </Label>

      <Card className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex size-16 shrink-0 items-center justify-center rounded-[20px] bg-[linear-gradient(140deg,#2DB4FF,#1C48AA)] text-[26px] font-extrabold">
            {c.firstName.slice(0, 1).toUpperCase()}
          </span>
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-baseline gap-2.5">
              <span className="text-2xl font-extrabold">
                {c.firstName} {c.lastName ?? ''}
              </span>
              {c.username ? <span className="text-base text-muted">@{c.username}</span> : null}
              {c.banned ? <Status tone="bad">бан: {c.banReason}</Status> : null}
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Chip>TG {c.telegramId}</Chip>
              <Chip>#{c.id}</Chip>
              <Chip>с {date(c.createdAt)}</Chip>
              <Chip>
                {c.referrer ? (
                  <>
                    по рефке{' '}
                    <Link to={`/users/${c.referrer.id}`}>{c.referrer.username ? `@${c.referrer.username}` : c.referrer.firstName}</Link>
                    {c.refSubId ? ` · ${c.refSubId}` : ''}
                  </>
                ) : (
                  `сам · ${c.source === 'bot' ? 'бот' : 'мини-апп'}`
                )}
              </Chip>
              <Chip tone={c.consentAt ? 'ok' : 'warn'}>{c.consentAt ? '18+ подтверждено' : 'нет согласия 18+'}</Chip>
              {!c.freeGenerationUsed ? <Chip tone="run">бесплатная не потрачена</Chip> : null}
              {c.moderation.confirmed30d || c.moderation.open || c.moderation.blocked24h ? (
                <Link to={`/moderation?status=all&user=${c.id}`}>
                  <Chip tone={c.moderation.confirmed30d >= MODERATION_RULES.strikesWarn ? 'bad' : 'warn'}>
                    нарушений за 30 дн: {c.moderation.confirmed30d}
                    {c.moderation.open ? ` · на проверке ${c.moderation.open}` : ''}
                    {c.moderation.blocked24h ? ` · автоблоков за сутки ${c.moderation.blocked24h}` : ''}
                  </Chip>
                </Link>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Button onClick={() => setDialog('message')}>Написать в бот</Button>
          {c.banned ? (
            <Button busy={busy === 'ban'} onClick={() => void run('ban', () => api.post(`/users/${c.id}/ban`, { banned: false }), 'Бан снят')}>
              Снять бан
            </Button>
          ) : (
            <Button variant="danger" onClick={() => setDialog('ban')}>
              Забанить
            </Button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
        {[
          ['Баланс', rub(c.balance), 'col-span-2 sm:col-span-1 bg-accent/10'],
          ['Пополнил(а)', rub(c.toppedUp), ''],
          ['Потратил(а)', rub(c.spent), ''],
          ['Генераций', num(c.generations), ''],
          ['Привёл(а) юзеров', num(c.referrals.length), ''],
        ].map(([k, v, cls]) => (
          <div key={k} className={cn('flex min-w-0 flex-col gap-1 rounded-2xl bg-field p-4', cls)}>
            <Label>{k}</Label>
            <b className="text-[22px]">{v}</b>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-start gap-5">
        <Card className="flex min-w-0 flex-[2_1_520px] flex-col gap-3.5">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn('h-10 cursor-pointer rounded-xl px-4 text-sm font-semibold whitespace-nowrap', tab === t.id ? 'bg-chip text-white' : 'text-soft hover:text-white')}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === 'ledger' ? (
            <>
              {c.ledger.length === 0 ? (
                <Empty>Записей нет</Empty>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <th>Когда</th>
                      <th>Операция</th>
                      <th>За что</th>
                      <th className="text-right!">Сумма</th>
                      <th className="text-right!">Баланс после</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.ledger.map((e) => {
                      const k = LEDGER[e.type] ?? LEDGER.hold!;
                      return (
                        <tr key={e.id}>
                          <td className="text-muted">{dateTime(e.createdAt)}</td>
                          <td>
                            <span className={cn('inline-flex h-7 items-center rounded-lg px-2.5 text-[13px]', k.chip)}>{k.label}</span>
                          </td>
                          <td className="max-w-[240px] truncate text-text2" title={e.comment ?? ''}>{e.comment ?? '—'}</td>
                          <td className={cn('text-right font-semibold', e.amount > 0 && k.amt)}>{signedRub(e.amount)}</td>
                          <td className="text-right text-muted">{rub(e.balanceAfter)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
              <Label>Баланс = сумма всех записей. Править можно только новой записью — старые не меняются.</Label>
            </>
          ) : null}
          {tab === 'gens' ? (
            <div className="flex flex-col gap-1">
              {!gens.data ? <Loading /> : null}
              {gens.data?.items.length === 0 ? <Empty>Генераций нет</Empty> : null}
              {gens.data?.items.map((g) => (
                <Link key={g.id} to={`/generations?id=${g.id}`} className="flex justify-between gap-3 rounded-xl px-3 py-3 text-sm text-white hover:bg-white/4">
                  <span className="truncate">
                    #{shortId(g.id)} · {g.title}
                  </span>
                  <span className="whitespace-nowrap text-muted">
                    {GEN_STATUS[g.status].label} · {genSum(g)}
                  </span>
                </Link>
              ))}
            </div>
          ) : null}
          {tab === 'refs' ? (
            <div className="flex flex-col gap-1">
              {c.referrals.length === 0 ? <Empty>Пока никого не привёл(а)</Empty> : null}
              {c.referrals.map((r) => (
                <Link key={r.id} to={`/users/${r.id}`} className="flex justify-between gap-3 rounded-xl px-3 py-3 text-sm text-white hover:bg-white/4">
                  <span className="truncate">
                    {r.username ? `@${r.username}` : r.firstName} · {r.paid ? `оплатил(а) ${rub(r.paid)}` : 'не платил(а)'}
                  </span>
                  <span className="text-muted">{date(r.createdAt)}</span>
                </Link>
              ))}
            </div>
          ) : null}
        </Card>

        <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-4">
          <Adjust user={c} busy={busy === 'adjust'} onSubmit={(body) => run('adjust', () => api.post(`/users/${c.id}/adjust`, body), 'Записано в журнал')} />
          <PartnerCard user={c} />
          <TwinCard user={c} />
          <Photos user={c} />
        </div>
      </div>

      <BanDialog open={dialog === 'ban'} onClose={() => setDialog(null)} busy={busy === 'ban'} onBan={(reason) => void run('ban', () => api.post(`/users/${c.id}/ban`, { banned: true, reason }), 'Забанен')} />
      <MessageDialog open={dialog === 'message'} onClose={() => setDialog(null)} busy={busy === 'msg'} onSend={(text) => void run('msg', () => api.post(`/users/${c.id}/message`, { text }), 'Отправлено')} />
    </>
  );
}

function Adjust({ user, busy, onSubmit }: { user: AdminUserCard; busy: boolean; onSubmit: (b: { amount: number; reason: string; key: string }) => Promise<boolean> }) {
  const [sign, setSign] = useState<1 | -1>(1);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [key, setKey] = useState(() => crypto.randomUUID());
  const n = Math.round(Number(amount) || 0);
  const ok = n > 0 && reason.trim().length >= 3 && (sign > 0 || n <= user.balance);

  return (
    <Card className="flex flex-col gap-3.5">
      <span className="text-base font-bold">Корректировка баланса</span>
      <div className="flex gap-2">
        <Button className={cn('flex-1', sign > 0 ? 'border-0 bg-ok/14 text-[#8FE8BF]' : 'text-soft')} onClick={() => setSign(1)}>
          + Начислить
        </Button>
        <Button className={cn('flex-1', sign < 0 ? 'border-0 bg-bad/14 text-bad' : 'text-soft')} onClick={() => setSign(-1)}>
          − Списать
        </Button>
      </div>
      <Field label="Сумма, ₽" hint={sign < 0 ? `на балансе ${rub(user.balance)}` : undefined}>
        <Input type="number" min={1} inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label="Причина (обязательно)">
        <Input placeholder="Например: компенсация за долгую очередь" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <Button
        variant="accent"
        disabled={!ok}
        busy={busy}
        onClick={async () => {
          if (await onSubmit({ amount: sign * n, reason, key })) {
            setAmount('');
            setReason('');
            setKey(crypto.randomUUID());
          }
        }}
      >
        Записать в журнал
      </Button>
      <Label>Уйдёт в журнал как «корректировка» и в аудит-лог с вашим именем.</Label>
    </Card>
  );
}

function Photos({ user }: { user: AdminUserCard }) {
  const toast = useToast();
  const [items, setItems] = useState<{ id: string; url: string | null }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [count, setCount] = useState(user.uploads);

  const reveal = async () => {
    setBusy(true);
    try {
      setItems((await api.post<{ items: { id: string; url: string | null }[] }>(`/users/${user.id}/uploads/reveal`)).items);
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ deleted: number; failed: number }>(`/users/${user.id}/uploads/delete`);
      toast(r.failed ? `Удалено ${r.deleted}, не удалось ${r.failed} — повторим вручную` : `Удалено фото: ${r.deleted}`, r.failed ? 'bad' : 'ok');
      setItems([]);
      setCount(0);
      setConfirm(false);
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="flex flex-col gap-3.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-bold">Фото юзера</span>
        <Chip>{count} фото</Chip>
      </div>
      {count === 0 ? (
        <span className="text-sm text-muted">Загруженных фото нет.</span>
      ) : items === null ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line-strong px-4 py-6 text-center">
          <span className="text-sm text-soft">Фото лиц скрыты — это персональные данные</span>
          <Button size="sm" busy={busy} onClick={() => void reveal()}>
            Показать (запишется в аудит)
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2">
            {items.map((p) => (p.url ? <img key={p.id} src={p.url} alt="" className="aspect-square w-full rounded-xl object-cover" /> : null))}
          </div>
          <Label>Просмотр записан в аудит. Ссылки живут 5 минут.</Label>
        </>
      )}
      {count > 0 ? (
        <Button variant="danger" onClick={() => setConfirm(true)}>
          Удалить фото по запросу
        </Button>
      ) : null}
      <Modal open={confirm} onClose={() => setConfirm(false)} title="Удалить все фото юзера?">
        <span className="text-sm text-soft">Файлы удалятся из хранилища без возможности восстановить. Готовые результаты генераций останутся до конца срока хранения.</span>
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => setConfirm(false)}>
            Отмена
          </Button>
          <Button variant="danger" className="flex-1" busy={busy} onClick={() => void del()}>
            Удалить
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

function PartnerCard({ user }: { user: AdminUserCard }) {
  const p = user.partner;
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-bold">Партнёрка</span>
        <Link to="/partners" className="text-[13px] font-semibold">
          Все заявки →
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2 text-sm">
        <span className="flex flex-col">
          <b className="text-lg">{rub(p.available)}</b>
          <span className="text-xs text-muted">доступно</span>
        </span>
        <span className="flex flex-col">
          <b className="text-lg">{rub(p.held)}</b>
          <span className="text-xs text-muted">в холде</span>
        </span>
        <span className="flex flex-col">
          <b className="text-lg">{rub(p.earned)}</b>
          <span className="text-xs text-muted">заработано</span>
        </span>
      </div>
      <span className="text-[13px] text-soft">
        Пришло {num(p.invited)} · купили {num(p.bought)}
        {p.postbackUrl ? (
          <>
            {' · постбэк: '}
            <span className="font-mono text-xs break-all text-text2">{p.postbackUrl}</span>
          </>
        ) : null}
      </span>
      {p.payouts.length ? (
        <div className="flex flex-col gap-1.5">
          {p.payouts.slice(0, 5).map((x) => (
            <span key={x.id} className="flex items-center justify-between gap-2 rounded-xl bg-field px-3 py-2 text-[13px]">
              <span>
                {METHOD[x.method]} · {rub(x.amount)} <span className="text-muted">· {date(x.createdAt)}</span>
              </span>
              <Status tone={PAYOUT_STATUS[x.status].tone}>{PAYOUT_STATUS[x.status].label}</Status>
            </span>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

const TWIN_STATUS = { photos: 'собирает фото', liveness: 'ждёт проверку живости', ready: 'готов' } as const;

function TwinCard({ user }: { user: AdminUserCard }) {
  const toast = useToast();
  const [twin, setTwin] = useState(user.twin);
  const [items, setItems] = useState<{ id: string; ok: boolean; url: string }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  if (!twin) {
    return (
      <Card className="flex flex-col gap-2">
        <span className="text-base font-bold">Двойник</span>
        <span className="text-sm text-muted">Не создавал.</span>
      </Card>
    );
  }
  const reveal = async () => {
    setBusy(true);
    try {
      setItems((await api.post<{ items: { id: string; ok: boolean; url: string }[] }>(`/users/${user.id}/twin/reveal`)).items);
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    setBusy(true);
    try {
      const r = await api.post<{ deleted: number }>(`/users/${user.id}/twin/delete`);
      toast(`Двойник удалён, фото: ${r.deleted}`);
      setTwin(null);
      setItems(null);
      setConfirm(false);
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  };
  const live = (['front', 'left', 'right'] as const).filter((p) => twin.liveness[p]).length;

  return (
    <Card className="flex flex-col gap-3.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-base font-bold">Двойник</span>
        <Status tone={twin.status === 'ready' ? 'ok' : 'warn'}>{TWIN_STATUS[twin.status]}</Status>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Chip>
          {twin.okPhotos} из {twin.photos} фото подошли
        </Chip>
        {twin.consistency !== null ? <Chip>сходство {twin.consistency}%</Chip> : null}
        <Chip>живость {live} из 3</Chip>
        {twin.readyAt ? <Chip>готов {date(twin.readyAt)}</Chip> : null}
      </div>
      {items === null ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line-strong px-4 py-6 text-center">
          <span className="text-sm text-soft">Фото двойника зашифрованы и скрыты</span>
          <Button size="sm" busy={busy} onClick={() => void reveal()}>
            Показать (запишется в аудит)
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-5 gap-2">
            {items.map((p) => (
              <img key={p.id} src={p.url} alt="" className={cn('aspect-square w-full rounded-xl object-cover', !p.ok && 'opacity-40 outline-2 outline-bad')} />
            ))}
          </div>
          <Label>Просмотр записан в аудит. Ссылки живут 5 минут.</Label>
        </>
      )}
      <Button variant="danger" onClick={() => setConfirm(true)}>
        Удалить двойника по запросу
      </Button>
      <Modal open={confirm} onClose={() => setConfirm(false)} title="Удалить двойника юзера?">
        <span className="text-sm text-soft">Фото и слепки лица удалятся без возможности восстановить. Юзер сможет создать двойника заново.</span>
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => setConfirm(false)}>
            Отмена
          </Button>
          <Button variant="danger" className="flex-1" busy={busy} onClick={() => void del()}>
            Удалить
          </Button>
        </div>
      </Modal>
    </Card>
  );
}

function BanDialog({ open, onClose, onBan, busy }: { open: boolean; onClose: () => void; onBan: (reason: string) => void; busy: boolean }) {
  const [reason, setReason] = useState('');
  return (
    <Modal open={open} onClose={onClose} title="Забанить юзера">
      <span className="text-sm text-soft">Не сможет запускать генерации, загружать фото и платить. Баланс сохранится.</span>
      <Field label="Причина (обязательно)">
        <Input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="Например: попытки генерировать запрещённое" />
      </Field>
      <Button variant="danger" disabled={reason.trim().length < 3} busy={busy} onClick={() => onBan(reason.trim())}>
        Забанить
      </Button>
    </Modal>
  );
}

function MessageDialog({ open, onClose, onSend, busy }: { open: boolean; onClose: () => void; onSend: (text: string) => void; busy: boolean }) {
  const [text, setText] = useState('');
  return (
    <Modal open={open} onClose={onClose} title="Написать в бот">
      <Textarea value={text} maxLength={3000} onChange={(e) => setText(e.target.value)} placeholder="Текст сообщения — придёт от бота" />
      <Button variant="accent" disabled={!text.trim()} busy={busy} onClick={() => onSend(text.trim())}>
        Отправить
      </Button>
    </Modal>
  );
}
